import React from "react";

import styled, { css } from "styled-components";

import { Link } from "gatsby";
import { Flex, Box } from "reflexbox";
import { OutboundLink as GAOutboundLink } from "gatsby-plugin-google-gtag";
import { useStaticQuery, graphql } from "gatsby";

import * as colors from "../styles/colors";
import * as values from "../styles/values";
import { OutboundLink } from "./Link";
import { maxContainerWidth } from "./PageContainer";
import { Newsletter } from "./Newsletter";

import DiscordIcon from "../images/icons/social/discord.inline.svg";
import TwitterIcon from "../images/icons/social/twitter.inline.svg";
import MediumIcon from "../images/icons/social/medium.inline.svg";
import GithubIcon from "../images/icons/social/github.inline.svg";
import InstagramIcon from "../images/icons/social/instagram.inline.svg";

const textColor = colors.secondaryText;
const backgroundColor = colors.darkThemeColor;

const bodyTextStyles = {
  fontSize: "12px",
  color: textColor,
  fontWeight: values.regularFontWeight
};

const footerLinkSharedStyle = css`
  font-size: 12px;
  margin: 0;
  padding: 2px 0;
  display: inline;
  text-decoration: none;
  font-weight: ${values.regularFontWeight};
  color: ${colors.dimmedLight};
  &:focus {
    box-shadow: none;
  }
  &:hover {
    background: radial-gradient(
      117.29% 1278% at 107.91% 94.25%,
      #0068c6 0%,
      #82fcff 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    cursor: pointer;
  }
`;

export const LinkContainer = styled(Link)`
  ${footerLinkSharedStyle}
`;

export const OutboundLinkContainer = styled(OutboundLink)`
  ${footerLinkSharedStyle}
`;

export const socialIcons = {
  discord: (props = {}) => <DiscordIcon {...props} />,
  twitter: (props = {}) => <TwitterIcon {...props} />,
  medium: (props = {}) => <MediumIcon {...props} />,
  github: (props = {}) => <GithubIcon {...props} />,
  instagram: (props = {}) => <InstagramIcon {...props} />
};

const HeadingContainer = styled(Box)`
  width: fit-content;
  font-size: 18px;
  font-weight: ${values.regularFontWeight};
  background-image: ${({ $accentColor }) => $accentColor};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;
`;

export function Footer({ accentColor = colors.blueGradient } = {}) {
  const {
    contentfulFooter: { socialMenu, websiteMenus }
  } = useStaticQuery(
    graphql`
      query {
        contentfulFooter(
          contentful_id: { eq: "3P2uuP4NMkK0934iuTeF4S" }
        ) {
          socialMenu {
            title
            links {
              id
              title
              layout
              type
              path
              openInNewTab
            }
          }
          websiteMenus {
            id
            title
            links {
              id
              title
              layout
              type
              path
              openInNewTab
            }
          }
        }
      }
    `
  );

  return (
    <Flex
      as="footer"
      className="page-footer"
      alignItems="center"
      justifyContent="center"
      mt={["6%", "40px", "50px", "60px"]}
      pb={["100px", "100px", "0", "0"]}
      style={{ background: backgroundColor }}
    >
      <Box
        flex={1}
        maxWidth={maxContainerWidth}
        px={["6%", "6%", 40]}
        my={["6%", "6%", 50]}
      >
        <Flex
          justifyContent="space-between"
          flexDirection={["column", "column", "column", "row"]}
        >
          <Flex
            flexDirection={["row", "row", "row", "column"]}
            flexWrap="wrap"
            pr={["0px", "0px", "0px", "100px"]}
            pb={["10px", "40px", "30px", "50px"]}
          >
            <Box
              flex={[1, 0.5, 0.5, 1]}
              minWidth={["100%", "50%", "50%", "auto"]}
              pb={["6%", "0px", "0px", "0px"]}
            >
              <SocialLinks
                title={socialMenu.title}
                links={socialMenu.links}
              />
            </Box>
            <Box
              flex={[1, 0.5, 0.5, 1]}
              minWidth={["100%", "50%", "50%", "auto"]}
              pb={["6%", "0px", "0px", "0px"]}
            >
              <Newsletter color={textColor} accentColor={accentColor} />
            </Box>
          </Flex>
          <Flex flex={1} justifyContent="space-between" flexWrap="wrap">
            {websiteMenus.map((menu) => (
              <LinkGroup
                key={menu.id}
                title={menu.title}
                links={menu.links}
                accentColor={accentColor}
              />
            ))}
          </Flex>
        </Flex>
        <Copyright />
      </Box>
    </Flex>
  );
}

function LinkGroup({ title, links, accentColor }) {
  return (
    <Flex
      flexDirection="column"
      pr={["20px", "0px", "0px", "0px"]}
      pb={["6%", "30px", "30px", "30px"]}
      flex={[0.5, 0.5, 0.25, 0.25]}
      minWidth={["100%", "50%", "auto", "auto"]}
    >
      <HeadingContainer mb={["16px"]} $accentColor={accentColor}>
        {title}
      </HeadingContainer>
      <Flex flexDirection="column">
        {links.map((link) => (
          <FooterLink key={link.id} {...link} />
        ))}
      </Flex>
    </Flex>
  );
}

function FooterLink({ title, type, path }) {
  return (
    <Box py={["6px"]}>
      {type === "href" ? (
        <OutboundLinkContainer
          type="link"
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={title}
        >
          {title}
        </OutboundLinkContainer>
      ) : (
        <LinkContainer to={path}>{title}</LinkContainer>
      )}
    </Box>
  );
}

function SocialLinks({ title, links }) {
  const isLast = (index) => links.length - 1 === index;
  return (
    <Flex pb={["14px"]} flexDirection="column">
      <HeadingContainer
        mb={["6%", "26px", "26px", "14px"]}
        display={["none", "inline-block", "inline-block", "none"]}
      >
        {title}
      </HeadingContainer>
      <Flex>
        {links.map(({ id, title, path }, index) => (
          <Box
            key={id}
            mr={isLast(index) ? 0 : "20px"}
            sx={{
              ":hover a svg": {
                fill: colors.primaryGreen
              }
            }}
          >
            <GAOutboundLink
              href={path}
              target="_blank"
              rel="noopener noreferrer"
              title={title}
              aria-label={title}
              style={{ padding: "6px 0" }}
            >
              {socialIcons?.[title.toLowerCase()]?.({
                fill: colors.whiteLightColor
              })}
            </GAOutboundLink>
          </Box>
        ))}
      </Flex>
    </Flex>
  );
}

function Copyright() {
  return (
    <Flex
      mt={["6%", 10, 10, 10]}
      pt={["6%", 40, 40, 40]}
      {...bodyTextStyles}
      flexDirection="column"
      style={{
        borderTop: `1px solid rgba(96, 108, 110, 0.23)`,
        fontSize: 12
      }}
    >
      Copyright © {new Date().getFullYear()} Neurosity, Inc. All rights
      reserved.
    </Flex>
  );
}
